/**
 * Seam loader — dynamic no-build plugin loading + registration shim.
 *
 * Loads single-file ESM plugin modules from a URL (same-origin path, https,
 * or IPFS-SW gateway path), builds their PluginContext, and registers their
 * kid renderers into the existing registry — TreeItem/BlockSettings untouched.
 */
import { Editor, Extension, generateJSON, mergeAttributes, Node } from '@tiptap/core'
import { Plugin as PmPlugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { deriveDeterministicEntityID } from '@wovin/core'
import { useSubscribable } from '@wovin/hooks-solid'
import { Logger } from 'besonders-logger'
import type { Component } from 'solid-js'
import { createEffect, createMemo, createSignal, For, getOwner, onCleanup, Show } from 'solid-js'
import html from 'solid-js/html'
import { Iconify } from '../../components/mini-components'
import { activePlugins, registerHiddenExtraPropPrefixes, registerKidRenderingPlugin, registerRootProvider } from '../registry'
import type { KidRenderingPlugin } from '../types'
import { defineSolidComponent, SeamHost } from './adapter-solid'
import type { PluginContext, PluginDef, PluginProvides, SeamKidRenderer, SeamPluginModule } from './contract'
import { HOST_CAPABILITIES, SEAM_API_VERSION } from './contract'
import { defaultRawThread, makeWovinApi } from './context'
import { makeRenderEntity } from './render-entity'

const { WARN, DEBUG } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

/**
 * Registry keyed by plugin IDENTITY — the deterministic entity ID derived from
 * the module source. Multiple URLs (gateway variants, ipfs:// vs https, IPNS vs
 * pinned CID) resolving to the same code dedupe to one plugin. (manu, round 6)
 */
const loadedPlugins = new Map<string, PluginDef>()
const urlToPluginId = new Map<string, string>()
export function getLoadedSeamPlugins(): ReadonlyMap<string, PluginDef> {
	return loadedPlugins
}

export function buildBaseContext(pluginUrl?: string, pluginId?: string): PluginContext {
	return {
		wovin: makeWovinApi(defaultRawThread),
		ui: {
			html: html as never,
			solid: { createEffect, createMemo, createSignal, For, getOwner, onCleanup, Show },
			useSubscribable: useSubscribable as never,
			defineSolidComponent,
			renderEntity: makeRenderEntity(defaultRawThread),
		},
		editor: {
			Tiptap: { Editor, Extension, generateJSON, mergeAttributes, Node },
			TiptapView: { Decoration, DecorationSet },
			ProseMirror: { Plugin: PmPlugin, PluginKey },
		},
		meta: {
			pluginUrl,
			pluginId,
			apiVersion: SEAM_API_VERSION,
			readOnly: () => !!defaultRawThread().readOnly,
		},
	}
}

const ALLOW_KEY = 'note3.seam.allowedPluginUrls'
function assertAllowedToLoad(url: string) {
	if (url.startsWith('/')) return // same-origin (public dir) — implicitly trusted in the PoC
	const list: string[] = JSON.parse(localStorage.getItem(ALLOW_KEY) ?? '[]')
	if (list.includes(url)) return
	// SECURITY: deliberate PoC stopgap — plugins run unsandboxed (see seam-design.md)
	if (!globalThis.confirm?.(`Load note3 plugin from:\n${url}\n\nIt will run with full access to your notes on this device.`)) {
		throw new Error(`[seam] Plugin load denied by user: ${url}`)
	}
	localStorage.setItem(ALLOW_KEY, JSON.stringify([...list, url]))
}

/**
 * Import a single-file ESM plugin module by URL.
 *
 * Under vite dev, a bare dynamic `import('/plugins/x.js')` is intercepted by
 * vite's transform middleware, which refuses to serve files from `/public`
 * ("should not be imported from source code"). To load no-build plugins we
 * fetch the source and import it via a blob URL — bypassing vite entirely.
 * (Works for same-origin public files and any CORS-readable URL; the plugin
 * must be self-contained, receiving all deps via ctx — no bare imports.)
 */
async function importPluginModule(url: string): Promise<{ mod: SeamPluginModule; src: string }> {
	const res = await fetch(url)
	if (!res.ok) throw new Error(`[seam] failed to fetch plugin ${url}: ${res.status} ${res.statusText}`)
	const src = await res.text()
	const blobUrl = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }))
	try {
		return { mod: await import(/* @vite-ignore */ blobUrl) as SeamPluginModule, src }
	} finally {
		URL.revokeObjectURL(blobUrl)
	}
}

export async function loadSeamPlugin(url: string): Promise<PluginDef> {
	const knownId = urlToPluginId.get(url)
	if (knownId) return loadedPlugins.get(knownId)!
	assertAllowedToLoad(url)
	DEBUG.force(`[seam] loading plugin:`, url)
	const { mod, src } = await importPluginModule(url)
	// identity = deterministic entity ID of the source — URL is only a fetch path
	const pluginId = deriveDeterministicEntityID(src)
	urlToPluginId.set(url, pluginId)
	const already = loadedPlugins.get(pluginId)
	if (already) {
		DEBUG.force(`[seam] plugin already loaded under different URL:`, { url, pluginId, name: already.name })
		return already
	}
	if (typeof mod.loadPlugin !== 'function') {
		throw new TypeError(`[seam] Module has no loadPlugin() export: ${url}`)
	}
	const ctx = buildBaseContext(url, pluginId)
	const def = await mod.loadPlugin(ctx)
	assertCapabilities(def)
	registerProvides(def, ctx)
	loadedPlugins.set(pluginId, def)
	DEBUG.force(`[seam] plugin registered:`, def.name, { pluginId, provides: Object.keys(providesOf(def)) })
	return def
}

function assertCapabilities(def: PluginDef) {
	// legacy PoC field — tolerated with a warning
	if (def.requiresApi && def.requiresApi > SEAM_API_VERSION) {
		throw new Error(`[seam] Plugin "${def.name}" requires seam API v${def.requiresApi}, host has v${SEAM_API_VERSION}`)
	}
	const unsupported = (def.requires ?? []).filter(cap => !(HOST_CAPABILITIES as readonly string[]).includes(cap))
	if (unsupported.length) {
		throw new Error(`[seam] Plugin "${def.name}" requires capabilities this host doesn't provide: ${unsupported.join(', ')}`)
	}
}

/** provides.* with legacy top-level kidRenderers folded in */
function providesOf(def: PluginDef): PluginProvides {
	const provides = { ...def.provides }
	if (def.kidRenderers?.length) {
		WARN(`[seam] Plugin "${def.name}" uses deprecated top-level kidRenderers — move to provides.kidRenderers`)
		provides.kidRenderers = [...(provides.kidRenderers ?? []), ...def.kidRenderers]
	}
	return provides
}

function registerProvides(def: PluginDef, ctx: PluginContext) {
	const provides = providesOf(def)
	for (const kidRenderer of provides.kidRenderers ?? []) {
		registerKidRenderingPlugin(seamKidRendererToPlugin(kidRenderer, ctx))
	}
	for (const rootProvider of provides.rootProviders ?? []) {
		registerRootProvider(rootProvider)
	}
	for (const extProvider of provides.editorExtensions ?? []) {
		activePlugins.push({ name: `${def.name}/${extProvider.name}`, tiptapExtensions: extProvider.extensions(ctx) as never[] })
	}
	if (provides.hiddenExtraPropPrefixes) {
		registerHiddenExtraPropPrefixes(provides.hiddenExtraPropPrefixes)
	}
}

function seamKidRendererToPlugin(kr: SeamKidRenderer, baseCtx: PluginContext): KidRenderingPlugin {
	const IconComp: Component<{ class?: string }> = props => <Iconify name={(kr.icon ?? 'puzzle-piece') as never} class={props.class} />
	return {
		type: kr.type,
		displayName: kr.displayName,
		Icon: IconComp,
		Root: props => (
			<SeamHost
				component={kr.root}
				baseCtx={baseCtx}
				props={{
					blockID: props.blockID,
					items: (props.items ?? []).map(it => ({ id: it.id, relationID: it.relationID, blockID: it.blockID })),
				}}
			/>
		),
		SingleKid: {
			name: `${kr.type}-seam-kid`,
			Renderer: () => null,
		},
	}
}
